import { i18n } from "@/utils/i18n";
import { Button } from "@aidergroup/ui-v2";
import {
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import React from "react";
import { Report } from "@/types/report";
import SectionTitle from "@/components/ui/section-title";
import { useFooterActions } from "./hooks";
import DownloadDialog from "./download-dialog";

const PRIMARY = "#009ac8";

const STATUS_OPTIONS = [1, 2, 4] as const;

const STATUS_ICONS: Record<number, React.ReactNode> = {
    1: <CheckCircleOutlineIcon sx={{ fontSize: 26 }} />,
    2: <EditNoteOutlinedIcon sx={{ fontSize: 26 }} />,
    3: <ReportProblemOutlinedIcon sx={{ fontSize: 26 }} />,
    4: <HelpOutlineIcon sx={{ fontSize: 26 }} />,
};

interface Props {
    reviewNotes: Report["reviewNotes"];
    reviewStatus: Report["reviewStatus"];
}

const Footer: React.FC<Props> = ({ reviewNotes, reviewStatus }) => {
    const defaultPages = React.useMemo(
        () => ["FRONT_PAGE", "ECONOMIC_SUMMARY", "ASSETS_DEBTS", "INCOMES_EXPENSES"],
        [],
    );
    const defaultReviewOutcome = "WITHOUT_REMARKS";
    const [downloadDialogOpen, setDownloadDialogOpen] = React.useState(false);
    const [selectedPages, setSelectedPages] = React.useState<string[]>(defaultPages);
    const [reviewOutcome, setReviewOutcome] = React.useState<string>(defaultReviewOutcome);
    const [pickedStatus, setPickedStatus] = React.useState<Report["reviewStatus"]>(reviewStatus);
    const {
        noteDialogOpen,
        selectedStatus,
        openNoteModal,
        closeNoteModal,
        submitReview,
        previewDownloading,
        downloadPreview,
    } = useFooterActions(reviewNotes);

    const togglePage = (page: string) => {
        setSelectedPages(prev =>
            prev.includes(page) ? prev.filter(i => i !== page) : [...prev, page],
        );
    };

    const onDownloadPreview = async () => {
        await downloadPreview(selectedPages, reviewOutcome);
        setDownloadDialogOpen(false);
    };

    const openDownloadDialog = () => {
        setSelectedPages(defaultPages);
        setReviewOutcome(defaultReviewOutcome);
        setDownloadDialogOpen(true);
    };

    return (
        <Box padding="24px 30px 24px 60px" display="flex" flexDirection="column" gap={2}>
            <SectionTitle title={i18n.t("reportReview.finalReviewTitle")} />
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                {i18n.t("reviewStatusSelect")}
            </Typography>

            <Box display="flex" gap={2} mb={3}>
                {STATUS_OPTIONS.map(status => {
                    const isActive = pickedStatus === status;

                    return (
                        <Box
                            key={status}
                            onClick={() => setPickedStatus(status)}
                            sx={{
                                flex: 1,
                                position: "relative",
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                padding: "16px",
                                borderRadius: "12px",
                                cursor: "pointer",
                                border: `2px solid ${isActive ? PRIMARY : "#e0e0e0"}`,
                                backgroundColor: isActive ? `${PRIMARY}0A` : "#fff",
                                transition: "all 0.15s ease",
                                "&:hover": {
                                    borderColor: PRIMARY,
                                    backgroundColor: `${PRIMARY}08`,
                                },
                            }}
                        >
                            {isActive && (
                                <CheckCircleIcon
                                    sx={{
                                        position: "absolute",
                                        top: 12,
                                        right: 12,
                                        fontSize: 22,
                                        color: PRIMARY,
                                    }}
                                />
                            )}

                            <Box sx={{ color: isActive ? PRIMARY : "text.secondary", mb: 0.5 }}>
                                {STATUS_ICONS[status]}
                            </Box>

                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: isActive ? PRIMARY : "text.primary",
                                    pr: isActive ? 3 : 0,
                                }}
                            >
                                {i18n.t(`reportStatus.${status}`)}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {i18n.t(`reportStatusDescription.${status}`)}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            <Box display="flex" alignItems="center" gap={2}>
                <Button
                    variant="contained"
                    onClick={() => openNoteModal(pickedStatus)}
                    disabled={!pickedStatus}
                >
                    {i18n.t("button.approve")}
                </Button>
                <Box display="flex" alignItems="center" gap={1}>
                    {previewDownloading && <CircularProgress size={18} />}
                    <Button
                        variant="outlined"
                        onClick={openDownloadDialog}
                        disabled={previewDownloading}
                    >
                        {i18n.t("button.downloadReportPreview")}
                    </Button>
                </Box>
            </Box>

            <DownloadDialog
                open={downloadDialogOpen}
                onClose={() => setDownloadDialogOpen(false)}
                reviewOutcome={reviewOutcome}
                onReviewOutcomeChange={setReviewOutcome}
                selectedPages={selectedPages}
                onTogglePage={togglePage}
                onDownload={onDownloadPreview}
                downloading={previewDownloading}
            />
            <Dialog
                open={noteDialogOpen}
                onClose={closeNoteModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: "16px" } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: "1.25rem", pb: 0.5 }}>
                    {i18n.t("alert.report.submit.title")}
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
                        {i18n.t("alert.report.submit.message")}
                    </Typography>
                    <Box
                        sx={{
                            backgroundColor: `${PRIMARY}0C`,
                            border: `1px solid ${PRIMARY}30`,
                            borderRadius: "12px",
                            padding: "16px 20px",
                        }}
                    >
                        <Typography variant="body2" sx={{ color: "text.primary", mb: 1 }}>
                            {i18n.t("alert.report.submit.notice")}
                        </Typography>
                        <Typography variant="body2" sx={{ color: PRIMARY, fontWeight: 600 }}>
                            {i18n.t("alert.report.submit.disclaimer")}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
                    <Button variant="outlined" onClick={closeNoteModal}>
                        {i18n.t("button.cancel")}
                    </Button>
                    <Button variant="contained" onClick={submitReview} disabled={!selectedStatus}>
                        {i18n.t("alert.report.submit.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Footer;
